import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { menuItems, moreMenuItems } from 'src/app/third-party/service-near/menu';
import { sideMenuItems, profileMenuItems } from 'src/assets/tobeonlineMenu';

@Component({
  selector: 'app-page404',
  templateUrl: './page404.component.html',
  styleUrls: ['./page404.component.scss']
})
export class Page404Component {
  constructor(private router: Router) {}

  // Cache the static items map so we don't rebuild it on every click
  private staticItemsMapCache?: Map<string, string>;

  private get allStaticItemsMap(): Map<string, string> {
    if (this.staticItemsMapCache) {
      return this.staticItemsMapCache;
    }
    
    // Merge ALL static menus together
    const serviceNearPaths = this.flattenStaticMenus(menuItems, moreMenuItems);
    const sellerPaths = this.flattenStaticMenus(sideMenuItems, [{ items: profileMenuItems }]);
    
    this.staticItemsMapCache = new Map([
      ...Array.from(sellerPaths.entries()),
      ...Array.from(serviceNearPaths.entries())
    ]);
    
    return this.staticItemsMapCache;
  }

  goBack() {
    const loginDetails = JSON.parse(localStorage.getItem('admin_Details') || '{}');
    const subscriptionDetails = JSON.parse(localStorage.getItem('SubscriptionFeatures') || '{}');
    const mobilemenuConfig = subscriptionDetails?.mobilemenu || [];
    
    const isServiceNear = loginDetails?.UserType === 'servicenear';
    
    // We prioritize the primary dashboard for the user type, but we can check both just in case.
    const primaryDashId = isServiceNear ? 'jobdashboard' : 'dashboard';
    const primaryDashPath = isServiceNear ? '/servicenear/dashboard' : '/dashboard/summary';
    const secondaryDashId = isServiceNear ? 'dashboard' : 'jobdashboard';
    const secondaryDashPath = isServiceNear ? '/dashboard/summary' : '/servicenear/dashboard';

    const checkDash = (id: string) => {
      const config = this.findConfigById(mobilemenuConfig, id);
      return config ? (config.show !== false && config.disable !== true) : true; // default true if missing
    };

    if (checkDash(primaryDashId)) {
      this.router.navigateByUrl(primaryDashPath);
      return;
    }
    if (checkDash(secondaryDashId)) {
      this.router.navigateByUrl(secondaryDashPath);
      return;
    }

    // Both dashboards are disabled. Find first valid fallback, excluding switcholdui
    const staticMap = this.allStaticItemsMap;
    
    // Sort config by positionindex
    const sortedConfig = [...mobilemenuConfig].sort((a: any, b: any) => 
      (a.positionindex ?? 999) - (b.positionindex ?? 999)
    );

    const firstEnabledItem = this.findFirstEnabledConfig(sortedConfig, staticMap);
    let validFallbackPath = null;
    
    if (firstEnabledItem) {
      validFallbackPath = firstEnabledItem.path || staticMap.get(firstEnabledItem.id);
    }
    
    this.router.navigateByUrl(validFallbackPath || '/');
  }

  private flattenStaticMenus(mainMenu: any[], moreMenu: any[]): Map<string, string> {
    const map = new Map<string, string>();
    const recurse = (items: any[]) => {
      if (!items) return;
      for (const item of items) {
        if (item.id && item.path && item.id !== 'switcholdui') {
          map.set(item.id, item.path);
        }
        if (item.children) recurse(item.children);
        if (item.items) recurse(item.items); // For moreMenuItems structure
      }
    };
    recurse(mainMenu);
    recurse(moreMenu);
    return map;
  }

  private findFirstEnabledConfig(configItems: any[], allStaticItemsMap: Map<string, string>): any {
    if (!configItems) return null;
    
    for (const item of configItems) {
      // Check if the current item (or folder) is enabled and visible
      if (item.show !== false && item.disable !== true && item.id !== 'switcholdui') {
        
        // 1. If it has a path itself, this is a valid leaf node. Return it.
        if (item.path || allStaticItemsMap.has(item.id)) {
          return item;
        }
        
        // 2. If it doesn't have a path, but it has children, search the children.
        // We ONLY search children if the parent folder is enabled!
        if (item.children?.length) {
          const childMatch = this.findFirstEnabledConfig(item.children, allStaticItemsMap);
          if (childMatch) return childMatch;
        }
      }
    }
    
    return null;
  }

  private findConfigById(configItems: any[], id: string): any {
    if (!configItems) return null;
    for (const item of configItems) {
      if (item.id === id) return item;
      if (item.children?.length) {
        const found = this.findConfigById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  }
}
